/**
 * API HOOKS IMPLEMENTATION RULES, THEY SHOULD ALWAYS REFLECT CURRENT ARCHITECTURE
 * - hook should execute only when input arg obj is changed (hook dependency)
 * - When input arg is null hook should treat it as special case and skip execution
 * - On Error Hooks should return response null and loading false on error
 * - API errors should be rethrown and handled in a catch, not in a response
 * - On Valid response it should set loading to false and reset error
 */

import { useCallback, useEffect, useState } from 'react';

import { ErrorMessageByCode, ErrorMessages } from '../domain/error-messages';
import { RequestParams } from '../services';
import { ApiGetResult } from './event-store-hooks';
import { HttpError, RpcError } from 'casper-js-sdk';

/**
 * @description This component is designed to be used to calculate loading flag from
 * various API utils by using their `loading` and `error` properties as input.
 * In dependent calls that are called in sequential order both loading and error props should be
 * declared together and in the correct order like this: `accountRawDataLoading || liquidBalanceLoading`.
 * @example
 * ```
 * const loading = checkApiHookIsLoading({
 *   loading: accountRawDataLoading || liquidBalanceLoading,
 *   error: accountRawDataError || liquidBalanceError,
 * })
 * ```
 */
export const checkApiHookIsLoading = ({
  loading,
  error,
}: {
  loading: boolean;
  error: string | undefined | null;
}) => loading && error == null;

/**
 * @descriptionuse create a Standard API hook from promise with passing request Params to promise
 */
export const createApiHookWithParams =
  <Entity extends any, Params extends RequestParams>(
    promise: (params: Params) => Promise<Entity>
  ) =>
  (requestParams: Params | null): ApiGetResult<Entity> => {
    const [requestCounter, setRequestCounter] = useState(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [response, setResponse] = useState<Entity | null>(null);

    const reloadData = useCallback(() => {
      setRequestCounter((state) => state + 1);
    }, []);

    useEffect(() => {
      if (requestParams == null) {
        return;
      }

      setLoading(true);
      promise(requestParams)
        .then((res) => {
          setLoading(false);
          setError(null);
          setResponse(res);
        })
        .catch((err: any) => {
          if (
            HttpError.isHttpError<RpcError>(err) &&
            ErrorMessageByCode[err.sourceErr.code]
          ) {
            setError(ErrorMessageByCode[err.sourceErr.code]);
          } else {
            setError(ErrorMessages.DefaulRpcErrorMessage);
          }
          setResponse(null);
          setLoading(false);
          // console.error(err);
        });
      /* eslint-disable-next-line */
    }, [requestCounter, JSON.stringify(requestParams)]);

    return { loading, error, data: response, reloadData };
  };

/**
 * @description will call promise using dependencies without passing request params
 */
export const useCallPromiseUsingDependencies = <Entity extends any>(
  promise: (() => Promise<Entity>) | undefined,
  dependencies: any[]
): ApiGetResult<Entity> => {
  const [requestCounter, setRequestCounter] = useState(0);
  const [payload, setPayload] = useState<{
    data: Entity | null;
    loading: boolean;
    error: string | null;
  }>({
    data: null,
    loading: true,
    error: null,
  });

  const reloadData = useCallback(() => {
    setRequestCounter((state) => state + 1);
  }, []);

  useEffect(() => {
    if (promise == null) {
      setPayload({
        data: null,
        loading: false,
        error: null,
      });
      return;
    }

    promise()
      .then((res) => {
        setPayload({
          data: res,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        setPayload({
          data: null,
          loading: false,
          error: ErrorMessageByCode[err.code]
            ? ErrorMessageByCode[err.code]
            : ErrorMessages.DefaulRpcErrorMessage,
        });
      });
    /* eslint-disable-next-line */
  }, [requestCounter, ...dependencies]);

  return { ...payload, reloadData };
};
